import React, { useState } from "react";
import {
  Drawer,
  Collapse,
  Divider,
  Box,
  Button,
  IconButton,
  InputBase,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import TableChartIcon from "@material-ui/icons/TableChart";
import { Link } from "react-router-dom";
import { Navigation } from "../lib";
import { REACT_APP_ADMIN_BRAND } from "../config";
import {
  preownedPalette,
  PREOWNED_PREFIX,
  PREOWNED_PT_FEATURES,
} from "../themes";
import { PreownedSwitchButton } from "./PreownedSwitchButton";

const SIDEBAR_WIDTH = 240;

// Grouped for scanning; every one is an `item_<type>` table in ptpreowned and
// is served by the same generic controller.
const ATTRIBUTE_GROUPS = [
  { label: "Model", types: ["brand", "size", "movement", "complication"] },
  {
    label: "Case & Dial",
    types: ["case_material", "case_back", "dial_color", "bezel", "crystal"],
  },
  { label: "Strap", types: ["strap_material", "strap_color", "buckle"] },
  {
    label: "Condition & Papers",
    types: ["condition", "box", "papers", "warranty", "factory_warranty"],
  },
  { label: "Accessories", types: ["accessory_type", "accessory_condition"] },
  { label: "Status", types: ["status"] },
];

const titleCase = (t) =>
  t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: {
    width: SIDEBAR_WIDTH,
    background: `linear-gradient(180deg, ${preownedPalette.slate} 0%, ${preownedPalette.slateLight} 100%)`,
    color: "#fff",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    borderRight: "none",
    boxShadow: "2px 0 8px rgba(0,0,0,0.18)",
  },
  // Amber rail down the drawer edge — the at-a-glance "not the main admin" cue.
  modeBar: {
    height: 3,
    backgroundColor: preownedPalette.amber,
    flexShrink: 0,
  },
  logoBox: {
    padding: theme.spacing(2, 2, 1.5),
  },
  logoText: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: "#fff",
    letterSpacing: "0.05em",
  },
  logoSub: {
    fontSize: "0.7rem",
    color: preownedPalette.amber,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  divider: {
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  addRow: {
    display: "flex",
    gap: 10,
    margin: theme.spacing(1.25, 1.5, 0),
  },
  addButton: {
    flex: 1,
    minWidth: 0,
    border: `1px solid ${preownedPalette.amber}`,
    color: preownedPalette.amber,
    fontSize: "0.76rem",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: 6,
    padding: "3px 4px",
    whiteSpace: "nowrap",
    "&:hover": {
      border: `1px solid ${preownedPalette.amber}`,
      backgroundColor: preownedPalette.amberDim,
    },
  },
  actionButton: {
    margin: theme.spacing(1.25, 1.5),
    width: `calc(100% - ${theme.spacing(3)}px)`,
    border: `1px solid ${preownedPalette.amber}`,
    color: preownedPalette.amber,
    fontSize: "0.82rem",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: 6,
    "&:hover": {
      border: `1px solid ${preownedPalette.amber}`,
      backgroundColor: preownedPalette.amberDim,
    },
  },
  searchPaper: {
    display: "flex",
    alignItems: "center",
    padding: "2px 6px",
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.15)",
    boxShadow: "none",
    borderRadius: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: "0.82rem",
    color: "#fff",
    "& input": {
      "&::placeholder": { color: "rgba(255,255,255,0.4)", opacity: 1 },
    },
  },
  searchIconBtn: {
    color: "rgba(255,255,255,0.55)",
    padding: 4,
    "&:hover": { color: "#fff" },
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.85, 2),
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.87)",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.09)",
    },
  },
  subHeader: {
    padding: theme.spacing(0.75, 2, 0.2, 2.5),
    fontSize: "0.67rem",
    fontWeight: 700,
    color: "rgba(255,255,255,0.38)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginTop: theme.spacing(0.5),
  },
  subLink: {
    display: "block",
    padding: theme.spacing(0.5, 2, 0.5, 3),
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.72)",
    textDecoration: "none",
    "&:hover": {
      backgroundColor: preownedPalette.amberDim,
      color: "#fff",
    },
  },
  subList: {
    backgroundColor: "rgba(0,0,0,0.15)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  navScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.2)",
      borderRadius: 2,
    },
  },
  bottomSection: {
    flexShrink: 0,
    padding: theme.spacing(1.5, 1.5, 2),
    borderTop: "1px solid rgba(255,255,255,0.1)",
  },
  logoutBtn: {
    width: "100%",
    color: "#f87171",
    fontSize: "0.82rem",
    textTransform: "none",
    borderRadius: 6,
    border: "1px solid rgba(248,113,113,0.4)",
    padding: theme.spacing(0.6, 1),
    "&:hover": {
      color: "#fca5a5",
      border: "1px solid rgba(248,113,113,0.75)",
      backgroundColor: "rgba(248,113,113,0.08)",
    },
  },
}));

function NavSection({ label, open, onToggle, children }) {
  const classes = useStyles();
  return (
    <>
      <div className={classes.navItem} onClick={onToggle}>
        <span>{label}</span>
        {open ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        )}
      </div>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.subList}>{children}</div>
      </Collapse>
    </>
  );
}

function SubHeader({ children }) {
  const classes = useStyles();
  return <div className={classes.subHeader}>{children}</div>;
}

function SubLink({ to, children }) {
  const classes = useStyles();
  return (
    <Link to={to} className={classes.subLink}>
      {children}
    </Link>
  );
}

/**
 * Sidebar for the pre-owned admin. Structure mirrors the main Sidebar so the
 * two feel like one product, but the palette is slate/amber rather than blue.
 * Nav contents are a skeleton pending the CodeIgniter port.
 */
export function PreownedSidebar() {
  const classes = useStyles();
  const [manageOpen, setManageOpen] = useState(false);
  const [watchSearch, setWatchSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const runSearch = () => {
    const term = watchSearch.trim();
    Navigation.go(
      term
        ? `${PREOWNED_PREFIX}/watches?search=${encodeURIComponent(term)}`
        : `${PREOWNED_PREFIX}/watches`,
    );
  };
  const [toolsOpen, setToolsOpen] = useState(false);

  function onLogoutClick() {
    if (!window.confirm("Are you sure you want to log out?")) return;
    Navigation.go("/auth/logout");
  }

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.modeBar} />

      <Box className={classes.logoBox}>
        <Typography className={classes.logoText}>
          {REACT_APP_ADMIN_BRAND}
        </Typography>
        <Typography className={classes.logoSub}>Pre-Owned Admin</Typography>
      </Box>

      <PreownedSwitchButton accent={preownedPalette.amber} />

      <Divider className={classes.divider} />

      <Box style={{ padding: "10px 12px 4px" }}>
        <Paper
          className={classes.searchPaper}
          style={{
            border: searchFocused ? "1px solid rgba(224,160,48,0.8)" : undefined,
          }}
        >
          <InputBase
            className={classes.searchInput}
            placeholder="Search pre-owned…"
            value={watchSearch}
            onChange={(e) => setWatchSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {watchSearch && (
            <IconButton
              size="small"
              className={classes.searchIconBtn}
              onClick={() => setWatchSearch("")}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            className={classes.searchIconBtn}
            onClick={runSearch}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>

      {/* Side by side: at 240px each button gets ~106px, which fits these
          labels but not "Add Watch" / "Add Accessory". */}
      <Box className={classes.addRow}>
        <Button
          variant="outlined"
          className={classes.addButton}
          component={Link}
          to={`${PREOWNED_PREFIX}/add-watch`}
        >
          + Watch
        </Button>
        <Button
          variant="outlined"
          className={classes.addButton}
          component={Link}
          to={`${PREOWNED_PREFIX}/add-accessory`}
        >
          + Accessory
        </Button>
      </Box>

      <Button
        variant="outlined"
        className={classes.actionButton}
        startIcon={<TableChartIcon style={{ fontSize: 15 }} />}
        component={Link}
        to={`${PREOWNED_PREFIX}/watches`}
      >
        Pre-Owned Watch Grid
      </Button>

      <Divider className={classes.divider} />

      <div className={classes.navScroll}>
        <NavSection
          label="Manage Watch"
          open={manageOpen}
          onToggle={() => setManageOpen(!manageOpen)}
        >
          {ATTRIBUTE_GROUPS.map((g) => (
            <React.Fragment key={g.label}>
              <SubHeader>{g.label}</SubHeader>
              {g.types.map((t) => (
                <SubLink key={t} to={`${PREOWNED_PREFIX}/attributes/${t}`}>
                  {titleCase(t)}
                </SubLink>
              ))}
            </React.Fragment>
          ))}
        </NavSection>

        <NavSection
          label="Tools"
          open={toolsOpen}
          onToggle={() => setToolsOpen(!toolsOpen)}
        >
          <SubLink to={`${PREOWNED_PREFIX}/generator`}>Email Generator</SubLink>
          <SubLink to={`${PREOWNED_PREFIX}/refresh-msrp`}>Refresh MSRP</SubLink>
          <SubLink to={`${PREOWNED_PREFIX}/sold-expires`}>Sold Expires At</SubLink>
          {PREOWNED_PT_FEATURES && (
            <SubLink to={`${PREOWNED_PREFIX}/tws-sync`}>Sync with TWS</SubLink>
          )}
        </NavSection>

      </div>

      <Box className={classes.bottomSection}>
        <Button
          variant="outlined"
          className={classes.logoutBtn}
          onClick={onLogoutClick}
        >
          Logout
        </Button>
      </Box>
    </Drawer>
  );
}
export default PreownedSidebar;
